import type { UserRole } from '@flame/shared';
import { useEffect, useState } from 'preact/hooks';
import { Api, type AdminUser } from '../../api';
import { CopyLink, formatDate, Spinner } from '../../components/ui';
import { attempt, user } from '../../state';

export function Users() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [role, setRole] = useState<UserRole>('member');
  const [link, setLink] = useState<{ label: string; url: string; expiresAt: string } | null>(null);

  const reload = async () => setUsers((await Api.users()).users);
  useEffect(() => {
    void attempt(reload);
  }, []);

  async function invite() {
    const res = await attempt(() => Api.createInvite(role));
    if (res) setLink({ label: `Invite link (${role})`, ...res });
  }

  async function reset(u: AdminUser) {
    if (
      !confirm(
        `Reset ${u.displayName}'s passkeys? They'll be signed out everywhere and need the recovery link to get back in.`
      )
    ) {
      return;
    }
    const res = await attempt(() => Api.resetPasskeys(u.id));
    if (res) setLink({ label: `Recovery link for ${u.username}`, ...res });
    await reload();
  }

  async function remove(u: AdminUser) {
    if (!confirm(`Delete ${u.displayName} and all of their links? This can't be undone.`)) return;
    await attempt(() => Api.deleteUser(u.id), 'User deleted.');
    await reload();
  }

  if (!users) return <Spinner />;
  return (
    <>
      <h2>Users</h2>
      <ul class="rows">
        {users.map((u) => (
          <li key={u.id} class="row">
            <div>
              <strong>
                {u.displayName} <span class="muted">({u.username})</span>{' '}
                {u.role === 'admin' && <span class="badge">admin</span>}
              </strong>
              <span class="muted">
                {u.passkeys} passkey{u.passkeys === 1 ? '' : 's'} · joined {formatDate(u.createdAt)}
              </span>
            </div>
            {u.id !== user.value?.id && (
              <span class="row-actions">
                <button type="button" class="button" onClick={() => reset(u)}>
                  Reset passkeys
                </button>
                <button type="button" class="button button-danger" onClick={() => remove(u)}>
                  Delete
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>

      <h3>Invite someone</h3>
      <p class="muted">
        Invite links work once and expire after 3 days. Each person gets their own page.
      </p>
      <div class="form-actions">
        <select value={role} onChange={(e) => setRole(e.currentTarget.value as UserRole)} aria-label="Role">
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>
        <button type="button" class="button button-primary" onClick={invite}>
          Create invite link
        </button>
      </div>
      {link && (
        <div class="notice">
          <strong>{link.label}</strong>, expires {formatDate(link.expiresAt)}. It's only shown
          once:
          <CopyLink url={link.url} />
        </div>
      )}
    </>
  );
}
